import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import CampaignService from '../services/campaignService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Users,
  Target,
  BarChart3,
  Package,
  Repeat,
  UserPlus,
} from 'lucide-react';
import {
  format,
  isToday,
  isBefore,
  startOfDay,
  differenceInDays,
} from 'date-fns';

const ModernCampaignReport: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { formatCurrency } = useCurrencyFormat();
  const campaignService = useMemo(() => new CampaignService(), []);

  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dailyBreakdownScrollRef = useRef<HTMLDivElement>(null);
  const todayDateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (campaignId) {
      loadReport();
    }
  }, [campaignId]);

  // Auto-scroll to today's date when data is loaded
  useEffect(() => {
    if (reportData && todayDateRef.current && dailyBreakdownScrollRef.current) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const scrollContainer = dailyBreakdownScrollRef.current;
        const todayElement = todayDateRef.current;

        if (scrollContainer && todayElement) {
          // Calculate the position of today's element relative to the scroll container
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = todayElement.getBoundingClientRect();

          // Calculate the scroll position needed to center today's element
          const scrollTop =
            scrollContainer.scrollTop +
            (elementRect.top - containerRect.top) -
            containerRect.height / 2 +
            elementRect.height / 2;

          // Scroll only the container, not the whole page
          scrollContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, [reportData]);

  const loadReport = async () => {
    if (!campaignId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await campaignService.getCampaignReport(campaignId);
      console.log('Campaign report data received:', data);
      console.log('Data structure:', {
        hasData: !!data,
        hasCampaign: !!data?.campaign,
        hasMetrics: !!data?.metrics,
        metricsKeys: data?.metrics ? Object.keys(data.metrics) : [],
        targetComparison: data?.metrics?.targetComparison,
      });

      // Handle response wrapped in data property
      const reportData = data?.data || data;
      if (!reportData || !reportData.campaign || !reportData.metrics) {
        console.error('Invalid report data structure:', reportData);
        setError('Invalid report data structure received from server');
        return;
      }

      setReportData(reportData);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.campaign_report.load_error')
      );
      console.error('Error loading campaign report:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.campaign_report.loading')}
        </div>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-500">
          {error || translate('pos.campaign_report.load_error')}
        </div>
        <Button onClick={() => navigate('/campaigns')} className="mt-4">
          {translate('pos.campaign_report.back_to_campaigns')}
        </Button>
      </div>
    );
  }

  const { campaign, metrics } = reportData;

  // Calculate campaign duration and days remaining
  const startDate = new Date(campaign.startDate);
  const endDate = new Date(campaign.endDate);
  const today = startOfDay(new Date());
  const campaignDuration = differenceInDays(endDate, startDate) + 1; // +1 to include both start and end days
  const daysRemaining = Math.max(0, differenceInDays(endDate, today) + 1); // +1 to include today if it's the end date
  const isCampaignEnded = isBefore(endDate, today);

  // Calculate percentage remaining
  const daysRemainingPercentage =
    campaignDuration > 0 ? (daysRemaining / campaignDuration) * 100 : 0;

  // Get progress bar color based on threshold
  const getProgressBarColor = (percentage: number) => {
    if (isCampaignEnded || percentage <= 0) return 'bg-red-500';
    if (percentage <= 25) return 'bg-red-500'; // Critical: < 25%
    if (percentage <= 50) return 'bg-orange-500'; // Warning: 25-50%
    if (percentage <= 75) return 'bg-yellow-500'; // Moderate: 50-75%
    return 'bg-green-500'; // Good: > 75%
  };

  const progressBarColor = getProgressBarColor(daysRemainingPercentage);

  // Defensive checks for metrics
  if (!metrics) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-500">
          {translate('pos.campaign_report.load_error')}: No metrics
          data
        </div>
        <Button onClick={() => navigate('/campaigns')} className="mt-4">
          {translate('pos.campaign_report.back_to_campaigns')}
        </Button>
      </div>
    );
  }

  const targetComparison = metrics.targetComparison || {
    revenueTarget: 0,
    revenueActual: 0,
    revenuePercentage: 0,
    categoryRevenueTarget: 0,
    categoryRevenueActual: 0,
    categoryRevenuePercentage: 0,
  };

  // Provide defaults for all metrics to prevent undefined errors
  const safeMetrics = {
    totalRevenue: metrics.totalRevenue || 0,
    campaignCategoryRevenue: metrics.campaignCategoryRevenue || 0,
    averageAOV: metrics.averageAOV || 0,
    averageDailyRevenue: metrics.averageDailyRevenue || 0,
    averageDailyCampaignRevenue: metrics.averageDailyCampaignRevenue || 0,
    totalItems: metrics.totalItems || 0,
    campaignCategoryItems: metrics.campaignCategoryItems || 0,
    averageDailySellThrough: metrics.averageDailySellThrough || 0,
    totalCustomers: metrics.totalCustomers || 0,
    repeatCustomers: metrics.repeatCustomers || 0,
    repeatPurchaseRate: metrics.repeatPurchaseRate || 0,
    newCustomers: metrics.newCustomers || 0,
    newCustomerPercentage: metrics.newCustomerPercentage || 0,
    dailyRevenue: metrics.dailyRevenue || {},
    dailyOrderCounts: metrics.dailyOrderCounts || {},
    productPerformance: metrics.productPerformance || {
      top20Percent: [],
      middle50Percent: [],
      bottom30Percent: [],
      all: [],
    },
    targetComparison,
  };

  // Calculate target averages
  // targetRevenue is the total target for the entire campaign period
  const targetRevenue =
    campaign.targetRevenue || targetComparison.revenueTarget || 0;
  const targetCategoryRevenue =
    campaign.targetCategoryRevenue ||
    targetComparison.categoryRevenueTarget ||
    0;
  // Target avg per 30 days = (total target / campaign duration) * 30
  const targetAvgPer30Days =
    campaignDuration > 0 ? (targetRevenue / campaignDuration) * 30 : 0;
  // Target avg per day is total target divided by campaign duration
  const targetAvgPerDay =
    campaignDuration > 0 ? targetRevenue / campaignDuration : 0;
  // Category target avg per day is category target divided by campaign duration
  const targetCategoryAvgPerDay =
    campaignDuration > 0 ? targetCategoryRevenue / campaignDuration : 0;

  // Calculate percentages for daily averages
  // Average Daily Revenue compares against total revenue target per day
  const avgDailyRevenuePercentage =
    targetAvgPerDay > 0
      ? (safeMetrics.averageDailyRevenue / targetAvgPerDay) * 100
      : 0;
  // Average Daily Campaign Revenue compares against category revenue target per day
  const avgDailyCampaignRevenuePercentage =
    targetCategoryAvgPerDay > 0
      ? (safeMetrics.averageDailyCampaignRevenue / targetCategoryAvgPerDay) *
        100
      : 0;

  const MetricCard = ({
    title,
    value,
    icon: Icon,
    subtitle,
    trend,
  }: {
    title: string;
    value: string | number | React.ReactNode;
    icon: any;
    subtitle?: string;
    trend?: { value: number; label: string; isPositive?: boolean };
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
        {trend && (
          <div className="flex items-center mt-2">
            <Badge
              variant={trend.isPositive ? 'default' : 'secondary'}
              className="text-xs"
            >
              {trend.label}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {translate('pos.campaign_report.back_to_campaigns')}
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{campaign.name}</h1>
          <p className="text-muted-foreground">
            {format(new Date(campaign.startDate), 'MMM d, yyyy')} -{' '}
            {format(new Date(campaign.endDate), 'MMM d, yyyy')}
          </p>
        </div>
      </div>

      {/* Campaign Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {campaign.description && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                {campaign.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Campaign Duration & Days Remaining */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {translate('pos.campaign_report.campaign_duration')}
                </span>
                <span className="text-lg font-bold">
                  {campaignDuration}{' '}
                  {translate('pos.campaign_report.days')}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate('pos.campaign_report.days_remaining')}
                  </span>
                  <span
                    className={`text-lg font-bold ${
                      isCampaignEnded
                        ? 'text-red-600'
                        : daysRemaining <= 7
                        ? 'text-orange-600'
                        : 'text-green-600'
                    }`}
                  >
                    {isCampaignEnded
                      ? translate('pos.campaign_report.ended')
                      : `${daysRemaining} ${translate(
                          'pos.campaign_report.days'
                        )}`}
                  </span>
                </div>
                {/* Progress Bar */}
                <div className="w-full">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">
                      {isCampaignEnded
                        ? '0%'
                        : `${Math.round(daysRemainingPercentage)}% ${translate(
                            'pos.campaign_report.remaining',
                            'remaining'
                          )}`}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {campaignDuration - daysRemaining} / {campaignDuration}{' '}
                      {translate('pos.campaign_report.days')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full ${progressBarColor} transition-all duration-500 ease-out rounded-full`}
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(100, daysRemainingPercentage)
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
              {targetRevenue > 0 && (
                <>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-sm font-medium text-muted-foreground">
                      {translate(
                        'pos.campaign_report.target_avg_per_30_days'
                      )}
                    </span>
                    <span className="text-lg font-bold">
                      <span data-sensitive-field="amount">
                        {formatCurrency(targetAvgPer30Days)}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      {translate(
                        'pos.campaign_report.target_avg_per_day'
                      )}
                    </span>
                    <span className="text-lg font-bold">
                      <span data-sensitive-field="amount">
                        {formatCurrency(targetAvgPerDay)}
                      </span>
                    </span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Metrics */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {translate('pos.campaign_report.revenue_metrics')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard
            title={translate('pos.campaign_report.total_revenue')}
            value={
              <span data-sensitive-field="amount">
                {formatCurrency(safeMetrics.totalRevenue)}
              </span>
            }
            icon={DollarSign}
            subtitle={translate(
              'pos.campaign_report.campaign_period_total'
            )}
            trend={{
              value: targetComparison.revenuePercentage,
              label: `${targetComparison.revenuePercentage.toFixed(
                1
              )}% ${translate('pos.campaign_report.of_target')}`,
              isPositive: targetComparison.revenuePercentage >= 100,
            }}
          />
          <MetricCard
            title={translate('pos.campaign_report.category_revenue')}
            value={
              <span data-sensitive-field="amount">
                {formatCurrency(safeMetrics.campaignCategoryRevenue)}
              </span>
            }
            icon={TrendingUp}
            subtitle={translate(
              'pos.campaign_report.campaign_category_sales'
            )}
            trend={{
              value: targetComparison.categoryRevenuePercentage,
              label: `${targetComparison.categoryRevenuePercentage.toFixed(
                1
              )}% ${translate('pos.campaign_report.of_target')}`,
              isPositive: targetComparison.categoryRevenuePercentage >= 100,
            }}
          />
          <MetricCard
            title={translate(
              'pos.campaign_report.average_order_value'
            )}
            value={
              <span data-sensitive-field="amount">
                {formatCurrency(safeMetrics.averageAOV)}
              </span>
            }
            icon={ShoppingCart}
            subtitle={translate(
              'pos.campaign_report.aov_during_campaign'
            )}
          />
          <MetricCard
            title={translate(
              'pos.campaign_report.average_daily_revenue'
            )}
            value={
              <span data-sensitive-field="amount">
                {formatCurrency(safeMetrics.averageDailyRevenue)}
              </span>
            }
            icon={BarChart3}
            subtitle={`${translate(
              'pos.campaign_report.daily_average_all_orders'
            )} | ${translate(
              'pos.campaign_report.target_avg_per_day'
            )}: ${formatCurrency(targetAvgPerDay)}`}
            trend={
              targetAvgPerDay > 0
                ? {
                    value: avgDailyRevenuePercentage,
                    label: `${avgDailyRevenuePercentage.toFixed(
                      1
                    )}% ${translate(
                      'pos.campaign_report.of_target'
                    )}`,
                    isPositive: avgDailyRevenuePercentage >= 100,
                  }
                : undefined
            }
          />
          <MetricCard
            title={translate(
              'pos.campaign_report.average_daily_campaign_revenue'
            )}
            value={
              <span data-sensitive-field="amount">
                {formatCurrency(safeMetrics.averageDailyCampaignRevenue)}
              </span>
            }
            icon={BarChart3}
            subtitle={
              targetCategoryAvgPerDay > 0
                ? `${translate(
                    'pos.campaign_report.daily_average_campaign_orders'
                  )} | ${translate(
                    'pos.campaign_report.target_avg_per_day'
                  )}: ${formatCurrency(targetCategoryAvgPerDay)}`
                : translate(
                    'pos.campaign_report.daily_average_campaign_orders'
                  )
            }
            trend={
              targetCategoryAvgPerDay > 0
                ? {
                    value: avgDailyCampaignRevenuePercentage,
                    label: `${avgDailyCampaignRevenuePercentage.toFixed(
                      1
                    )}% ${translate(
                      'pos.campaign_report.of_target'
                    )}`,
                    isPositive: avgDailyCampaignRevenuePercentage >= 100,
                  }
                : undefined
            }
          />
        </div>
      </div>

      {/* Product Performance */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {translate('pos.campaign_report.product_performance')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title={translate('pos.campaign_report.total_items_sold')}
            value={safeMetrics.totalItems.toLocaleString()}
            icon={Package}
            subtitle={translate(
              'pos.campaign_report.campaign_items_sold'
            )}
          />
          <MetricCard
            title={translate(
              'pos.campaign_report.campaign_items_sold'
            )}
            value={safeMetrics.campaignCategoryItems.toLocaleString()}
            icon={Package}
            subtitle={translate(
              'pos.campaign_report.campaign_category_products'
            )}
          />
          <MetricCard
            title={translate(
              'pos.campaign_report.daily_sell_through'
            )}
            value={safeMetrics.averageDailySellThrough.toFixed(1)}
            icon={TrendingUp}
            subtitle={translate(
              'pos.campaign_report.average_units_per_day'
            )}
          />
        </div>

        {/* Top Products */}
        {safeMetrics.productPerformance.top20Percent.length > 0 && (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>
                {translate('pos.campaign_report.top_products')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {safeMetrics.productPerformance.top20Percent.map(
                  (product: any) => (
                    <div
                      key={product.productId}
                      className="flex items-center justify-between p-2 border rounded"
                    >
                      <div>
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {product.quantity}{' '}
                          {translate('pos.campaign_report.units')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          <span data-sensitive-field="amount">
                            {formatCurrency(product.revenue)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Customer Metrics */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {translate('pos.campaign_report.customer_metrics')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title={translate('pos.campaign_report.total_customers')}
            value={safeMetrics.totalCustomers}
            icon={Users}
            subtitle={translate(
              'pos.campaign_report.unique_customers'
            )}
          />
          <MetricCard
            title={translate(
              'pos.campaign_report.repeat_purchase_rate'
            )}
            value={`${(safeMetrics.repeatPurchaseRate * 100).toFixed(1)}%`}
            icon={Repeat}
            subtitle={`${safeMetrics.repeatCustomers} ${translate(
              'pos.campaign_report.repeat_customers'
            )}`}
            trend={{
              value: safeMetrics.repeatPurchaseRate * 100,
              label: `${translate(
                'pos.campaign_report.target'
              )}: 40%`,
              isPositive: safeMetrics.repeatPurchaseRate >= 0.4,
            }}
          />
          <MetricCard
            title={translate('pos.campaign_report.new_customers')}
            value={safeMetrics.newCustomers}
            icon={UserPlus}
            subtitle={`${safeMetrics.newCustomerPercentage.toFixed(
              1
            )}% ${translate('pos.campaign_report.of_total')}`}
          />
        </div>
      </div>

      {/* Target Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.campaign_report.target_vs_actual')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {translate('pos.campaign_report.total_revenue')}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  <span data-sensitive-field="amount">
                    {formatCurrency(safeMetrics.totalRevenue)}
                  </span>{' '}
                  /{' '}
                  <span data-sensitive-field="amount">
                    {formatCurrency(targetComparison.revenueTarget)}
                  </span>
                </span>
                <Badge
                  variant={
                    targetComparison.revenuePercentage >= 100
                      ? 'default'
                      : 'secondary'
                  }
                >
                  {targetComparison.revenuePercentage.toFixed(1)}%
                </Badge>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  targetComparison.revenuePercentage >= 100
                    ? 'bg-green-500'
                    : 'bg-blue-500'
                }`}
                style={{
                  width: `${Math.min(
                    targetComparison.revenuePercentage,
                    100
                  )}%`,
                }}
              />
            </div>
          </div>

          {targetComparison.categoryRevenueTarget > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {translate('pos.campaign_report.category_revenue')}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    <span data-sensitive-field="amount">
                      {formatCurrency(safeMetrics.campaignCategoryRevenue)}
                    </span>{' '}
                    /{' '}
                    <span data-sensitive-field="amount">
                      {formatCurrency(targetComparison.categoryRevenueTarget)}
                    </span>
                  </span>
                  <Badge
                    variant={
                      targetComparison.categoryRevenuePercentage >= 100
                        ? 'default'
                        : 'secondary'
                    }
                  >
                    {targetComparison.categoryRevenuePercentage.toFixed(1)}%
                  </Badge>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    targetComparison.categoryRevenuePercentage >= 100
                      ? 'bg-green-500'
                      : 'bg-blue-500'
                  }`}
                  style={{
                    width: `${Math.min(
                      targetComparison.categoryRevenuePercentage,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Average Daily Revenue */}
          {targetAvgPerDay > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {translate(
                    'pos.campaign_report.average_daily_revenue'
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    <span data-sensitive-field="amount">
                      {formatCurrency(safeMetrics.averageDailyRevenue)}
                    </span>{' '}
                    /{' '}
                    <span data-sensitive-field="amount">
                      {formatCurrency(targetAvgPerDay)}
                    </span>
                  </span>
                  <Badge
                    variant={
                      avgDailyRevenuePercentage >= 100 ? 'default' : 'secondary'
                    }
                  >
                    {avgDailyRevenuePercentage.toFixed(1)}%
                  </Badge>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    avgDailyRevenuePercentage >= 100
                      ? 'bg-green-500'
                      : 'bg-blue-500'
                  }`}
                  style={{
                    width: `${Math.min(avgDailyRevenuePercentage, 100)}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Average Daily Campaign Revenue */}
          {targetCategoryAvgPerDay > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {translate(
                    'pos.campaign_report.average_daily_campaign_revenue'
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    <span data-sensitive-field="amount">
                      {formatCurrency(safeMetrics.averageDailyCampaignRevenue)}
                    </span>{' '}
                    /{' '}
                    <span data-sensitive-field="amount">
                      {formatCurrency(targetCategoryAvgPerDay)}
                    </span>
                  </span>
                  <Badge
                    variant={
                      avgDailyCampaignRevenuePercentage >= 100
                        ? 'default'
                        : 'secondary'
                    }
                  >
                    {avgDailyCampaignRevenuePercentage.toFixed(1)}%
                  </Badge>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    avgDailyCampaignRevenuePercentage >= 100
                      ? 'bg-green-500'
                      : 'bg-blue-500'
                  }`}
                  style={{
                    width: `${Math.min(
                      avgDailyCampaignRevenuePercentage,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.campaign_report.daily_breakdown')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            ref={dailyBreakdownScrollRef}
            className="space-y-2 max-h-96 overflow-y-auto"
          >
            {Object.entries(safeMetrics.dailyRevenue)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, revenue]: [string, any]) => {
                const dateObj = new Date(date);
                const isTodayDate = isToday(dateObj);
                const isPastDate = isBefore(
                  startOfDay(dateObj),
                  startOfDay(new Date())
                );

                return (
                  <div
                    key={date}
                    ref={isTodayDate ? todayDateRef : null}
                    className={`flex items-center justify-between p-2 border rounded transition-all ${
                      isTodayDate
                        ? 'bg-primary/10 border-primary border-2 shadow-sm'
                        : isPastDate
                        ? 'opacity-50 bg-muted/30'
                        : ''
                    }`}
                  >
                    <div>
                      <div
                        className={`font-medium ${
                          isTodayDate ? 'text-primary font-bold' : ''
                        }`}
                      >
                        {format(dateObj, 'MMM d, yyyy')}
                      </div>
                      <div
                        className={`text-sm ${
                          isTodayDate
                            ? 'text-primary/80'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {safeMetrics.dailyOrderCounts[date] || 0}{' '}
                        {translate('pos.campaign_report.orders')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`font-medium ${
                          isTodayDate ? 'text-primary font-bold' : ''
                        }`}
                      >
                        <span data-sensitive-field="amount">
                          {formatCurrency(revenue || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernCampaignReport;
